/**
 * Interpretação da resposta do endpoint /event/batch.
 */

import type { BatchEventResponse } from '../types';

function toIdList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string');
}

export function normalizeBatchResponse(body: unknown): BatchEventResponse {
  if (body == null || typeof body !== 'object') {
    throw new Error('Invalid batch response body');
  }
  const raw = body as Record<string, unknown>;
  if (typeof raw.success !== 'boolean') {
    throw new Error('Invalid batch response body');
  }
  return {
    success: raw.success,
    batch_id: typeof raw.batch_id === 'string' ? raw.batch_id : undefined,
    processed: typeof raw.processed === 'number' ? raw.processed : undefined,
    failed: typeof raw.failed === 'number' ? raw.failed : undefined,
    processed_ids: toIdList(raw.processed_ids),
    failed_ids: toIdList(raw.failed_ids),
    duplicated_ids: toIdList(raw.duplicated_ids),
    ignored_ids: toIdList(raw.ignored_ids),
  };
}

/** ACK estrito: apenas IDs explicitamente confirmados pelo backend. */
export function getExplicitSettledIds(
  response: BatchEventResponse
): Set<string> {
  return new Set([
    ...response.processed_ids,
    ...response.duplicated_ids,
    ...response.ignored_ids,
  ]);
}

export function hasExplicitAckLists(response: BatchEventResponse): boolean {
  return (
    response.processed_ids.length > 0 ||
    response.duplicated_ids.length > 0 ||
    response.ignored_ids.length > 0 ||
    response.failed_ids.length > 0
  );
}

/** IDs enviados no batch sem confirmação explícita (nunca remover da fila). */
export function getAmbiguousAckIds(
  batchItemIds: string[],
  response: BatchEventResponse
): string[] {
  if (!hasExplicitAckLists(response)) return [];
  const explicit = getExplicitSettledIds(response);
  const failed = new Set(response.failed_ids);
  return batchItemIds.filter(id => !explicit.has(id) && !failed.has(id));
}

/**
 * IDs finalizados. Modo estrito: só listas explícitas.
 * Legado (sem listas + success): todos exceto failed_ids.
 */
export function getSettledEventIds(
  response: BatchEventResponse,
  batchItemIds: string[],
  strictAck = true
): Set<string> {
  const explicit = getExplicitSettledIds(response);
  if (explicit.size > 0) return explicit;
  if (strictAck) return new Set();
  if (!response.success) return new Set();
  const failed = new Set(response.failed_ids);
  return new Set(batchItemIds.filter(id => !failed.has(id)));
}

export function getFailedEventIds(response: BatchEventResponse): Set<string> {
  return new Set(response.failed_ids);
}

export function getItemsToRetry<T extends { id: string }>(
  batchItems: T[],
  response: BatchEventResponse,
  strictAck = true
): T[] {
  if (strictAck && response.success && !hasExplicitAckLists(response)) {
    return [...batchItems];
  }
  const batchIds = batchItems.map(i => i.id);
  const failed = getFailedEventIds(response);
  const settled = getSettledEventIds(response, batchIds, strictAck);
  const ambiguous = new Set(getAmbiguousAckIds(batchIds, response));

  return batchItems.filter(item => {
    if (failed.has(item.id)) return true;
    if (ambiguous.has(item.id)) return true;
    if (settled.has(item.id)) return false;
    if (!hasExplicitAckLists(response) && response.success && !strictAck)
      return false;
    return !response.success;
  });
}
